import React, {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from "react";
import type { ReactElement, ReactNode } from "react";
import type { TextFieldRef } from "@react-types/textfield";
import { Flex, TextField, View } from "../spectrumCompat";
import "./DxpPhoneNumberField.css";

export type DxpPhoneNumberFieldProps = {
  /**
   * Country / dial-code control (typically S2 `Picker`). Rendered in a fixed-width
   * column so it matches Spectrum patterns next to the number field.
   */
  countrySelector: ReactNode;
  /**
   * Dial code shown in the fused prefix before the number field (e.g. "+91"),
   * matching legacy `CustomPhoneNumberRenderer`.
   */
  dialPrefix?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Applied to the inner input (parent usually shows the visible field label above the row). */
  "aria-label": string;
  isInvalid?: boolean;
  isDisabled?: boolean;
  maxLength?: number;
  size?: "S" | "M" | "L" | "XL";
  /** Fixed width for the country column (px). Default 80 matches legacy layout. */
  pickerColumnWidth?: number;
  UNSAFE_className?: string;
};

export type DxpPhoneNumberFieldRef = Pick<
  TextFieldRef<HTMLInputElement>,
  "getInputElement" | "focus"
>;

const PICKER_ROOT_CLASS = "dxp-phone-field__picker-root";

/**
 * Merges layout class onto the country Picker and clears inline help/error on it.
 * S2 Picker renders HelpText inside the Select root; with our fused row layout that
 * becomes a second column flex item and blows the picker column height. Validation
 * should surface on the number field and/or copy below the composite.
 */
function prepareCountrySelector(node: ReactNode): ReactNode {
  if (!React.isValidElement(node)) {
    return node;
  }
  const el = node as ReactElement<{
    UNSAFE_className?: string;
    className?: string;
    isInvalid?: boolean;
    errorMessage?: ReactNode;
    description?: ReactNode;
  }>;
  const merged = [
    PICKER_ROOT_CLASS,
    el.props.UNSAFE_className,
    el.props.className,
  ]
    .filter(Boolean)
    .join(" ");
  return React.cloneElement(el, {
    ...el.props,
    UNSAFE_className: merged,
    isInvalid: false,
    errorMessage: undefined,
    description: undefined,
  });
}

const DxpPhoneNumberField = forwardRef<
  DxpPhoneNumberFieldRef,
  DxpPhoneNumberFieldProps
>((props, ref) => {
  const {
    countrySelector,
    dialPrefix,
    value,
    onChange,
    placeholder: _placeholder = "Enter phone number",
    "aria-label": ariaLabel,
    isInvalid,
    isDisabled,
    maxLength,
    size = "M",
    pickerColumnWidth = 100,
    UNSAFE_className: rootClassName = "",
  } = props;

  const textFieldRef = useRef<TextFieldRef<HTMLInputElement>>(null);
  const rootFlexRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = rootFlexRef.current;
    if (!el) return undefined;
    const px = `${pickerColumnWidth}px`;
    el.style.setProperty("--dxp-phone-picker-col-width", px);
    return undefined;
  }, [pickerColumnWidth]);

  useImperativeHandle(ref, () => ({
    focus: () => {
      textFieldRef.current?.focus?.();
    },
    getInputElement: () => textFieldRef.current?.getInputElement?.() ?? null,
  }));

  const hasDial = Boolean(dialPrefix?.trim());

  return (
    <Flex
      ref={rootFlexRef}
      direction="row"
      alignItems="stretch"
      UNSAFE_className={["dxp-phone-field__root", rootClassName]
        .filter(Boolean)
        .join(" ")}
    >
      <View UNSAFE_className="dxp-phone-field__picker-col">
        <View UNSAFE_className="dxp-phone-field__picker-inner">
          {prepareCountrySelector(countrySelector)}
        </View>
      </View>
      <View UNSAFE_className="dxp-phone-field__suffix-col">
        <div
          className={[
            "dxp-phone-field__numberRow",
            hasDial
              ? "dxp-phone-field__numberRow--withPrefix"
              : "dxp-phone-field__numberRow--noPrefix",
          ].join(" ")}
        >
          {hasDial ? (
            <div className="dxp-phone-field__dialPrefix">
              {String(dialPrefix).trim()}
            </div>
          ) : null}
          <div className="dxp-phone-field__textFieldWrap">
            <TextField
              ref={textFieldRef}
              aria-label={ariaLabel}
              value={value}
              onChange={onChange}
              type="tel"
              isInvalid={isInvalid}
              isDisabled={isDisabled}
              maxLength={maxLength}
              size={size}
              errorMessage={undefined}
              UNSAFE_className="dxp-phone-field__spectrum-text-field"
            />
          </div>
        </div>
      </View>
    </Flex>
  );
});

DxpPhoneNumberField.displayName = "DxpPhoneNumberField";

export default DxpPhoneNumberField;
